import React, { useEffect, useState } from 'react';
import AskIcon from 'shared/components/ui/AskIcon';
import { useParams } from 'react-router-dom';
import { ExternalLink, Pause, Pencil, Play, Zap } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { getSelfImproving, deploySelfImproving, updateSelfImproving, setSelfImprovingLive, runSelfImprovingNow } from 'features/evals3/api/evals3Api';

/**
 * Self-improving on one eval.
 *
 * Deploying it makes two ordinary things, each editable where it lives: an
 * ALERT on the eval (when to look) and an AGENT that starts from it and has
 * Ask improve the eval — a proposed change in Proposed changes, and what to
 * fix in the agent. The panel reads whether that pair is live; it stores
 * nothing of its own.
 */

const MEASURES = [
  { value: 'failures', label: 'Failures', unit: 'count' },
  { value: 'pass_rate', label: 'Pass rate', unit: 'percent' },
  { value: 'thumbs_down', label: 'Results marked wrong', unit: 'count' },
  { value: 'audit_disagreements', label: 'Audit disagreements', unit: 'count' },
  { value: 'errored', label: 'Errored', unit: 'count' },
  { value: 'waiting', label: 'Waiting for a person', unit: 'count' },
  { value: 'score', label: 'Average score', unit: 'score' },
];
const OPERATORS = [
  { value: 'at_or_above', label: 'at or above' }, { value: 'above', label: 'above' },
  { value: 'below', label: 'below' }, { value: 'at_or_below', label: 'at or below' },
];
const WINDOWS = [
  { value: '900', label: '15 minutes' }, { value: '3600', label: '1 hour' }, { value: '21600', label: '6 hours' },
  { value: '86400', label: '24 hours' }, { value: '604800', label: '7 days' },
];
const measureWord = (m) => MEASURES.find((x) => x.value === m)?.label || m;
const opWord = (o) => OPERATORS.find((x) => x.value === o)?.label || o;
const windowWord = (s) => WINDOWS.find((x) => Number(x.value) === Number(s))?.label || `${Math.round(Number(s) / 60)} minutes`;
const valueWord = (m, v) => (m === 'pass_rate' ? `${v}%` : String(v));

const numField = 'h-7 w-20 shrink-0 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]';
const btn = 'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12px] font-medium transition-colors disabled:opacity-60';

export default function SelfImprovingPanel({ teamId, lineageId, readOnly = false }) {
  const { team: teamSlug } = useParams();
  const [st, setSt] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null); // open deploy form
  const [busy, setBusy] = useState(false);
  const [ran, setRan] = useState(null);

  useEffect(() => {
    let off = false;
    if (!teamId || !lineageId) return undefined;
    getSelfImproving(teamId, lineageId).then((d) => { if (!off) setSt(d); }).catch((e) => { if (!off) setError(e?.response?.data?.message || e.message); });
    return () => { off = true; };
  }, [teamId, lineageId]);

  const slug = teamSlug || teamId;
  const openForm = () => {
    const c = st?.defaults?.condition || { measure: 'failures', operator: 'at_or_above', threshold: 5, window_seconds: 3600 };
    setForm({ measure: c.measure, operator: c.operator, threshold: String(c.threshold), window_seconds: String(c.window_seconds), instructions: st?.defaults?.instructions || '', paused: false });
  };
  const openEdit = () => {
    const p = st.pairs[0];
    setForm({
      editing: true, measure: p.alert.measure, operator: p.alert.operator, threshold: String(p.alert.threshold),
      window_seconds: String(p.alert.window_seconds), instructions: p.agent?.instructions || st.defaults?.instructions || '', paused: false,
    });
  };
  const deploy = async () => {
    setBusy(true); setError(null);
    try {
      const body = {
        measure: form.measure, operator: form.operator, threshold: Number(form.threshold), window_seconds: Number(form.window_seconds),
        instructions: form.instructions,
      };
      const out = form.editing
        ? await updateSelfImproving(teamId, lineageId, { ...body, alert_id: st.pairs[0].alert.id })
        : await deploySelfImproving(teamId, lineageId, { ...body, paused: form.paused });
      setSt(out); setForm(null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true); setError(null); setRan(null);
    try {
      const out = await runSelfImprovingNow(teamId, lineageId);
      setRan(`Running now. The change it proposes will appear in Proposed changes in a minute or two.`);
      return out;
    } catch (e) { setError(e?.response?.data?.message || e.message); return null; } finally { setBusy(false); }
  };
  const setLive = async (live) => {
    setBusy(true); setError(null);
    try { setSt(await setSelfImprovingLive(teamId, lineageId, live)); } catch (e) { setError(e?.response?.data?.message || e.message); } finally { setBusy(false); }
  };

  if (!st && !error) return <div className="text-[12px] text-[color:var(--text-muted)]">Checking…</div>;
  const pairs = st?.pairs || [];
  const editing = Boolean(form?.editing);

  const formEl = form ? (
        <div className="grid gap-2 rounded-md border border-[color:var(--border-default)] p-3" data-testid="self-improving-form">
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-[color:var(--text-secondary)]">
            <span>When</span>
            <div className="w-44"><BrandDropdown compact ariaLabel="Measure" value={form.measure} onChange={(v) => setForm((f) => ({ ...f, measure: v }))} options={MEASURES.map((m) => ({ value: m.value, label: m.label }))} /></div>
            <span>is</span>
            <div className="w-28"><BrandDropdown compact ariaLabel="Operator" value={form.operator} onChange={(v) => setForm((f) => ({ ...f, operator: v }))} options={OPERATORS} /></div>
            <input type="number" value={form.threshold} aria-label="Threshold" onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))} className={numField} />
            {form.measure === 'pass_rate' && <span>%</span>}
            <span>over</span>
            <div className="w-32"><BrandDropdown compact ariaLabel="Window" value={form.window_seconds} onChange={(v) => setForm((f) => ({ ...f, window_seconds: v }))} options={WINDOWS} /></div>
          </div>
          <div>
            <div className="mb-1 text-[11px] text-[color:var(--text-muted)]">What Lucid should do (you can change it later on the agent)</div>
            <textarea value={form.instructions} rows={4} aria-label="Instructions" onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))} className="input w-full text-[12px]" />
          </div>
          {editing ? null : (
            <label className="flex items-center gap-2 text-[12px] text-[color:var(--text-secondary)]">
              <input type="checkbox" checked={form.paused} onChange={(e) => setForm((f) => ({ ...f, paused: e.target.checked }))} aria-label="Start paused" /> Start paused
            </label>
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={deploy} disabled={busy || !form.threshold} className={`${btn} bg-brand text-ink-onBrand hover:bg-brand-hover`}>
              {editing ? 'Save' : <><AskIcon size={13} /> Deploy</>}
            </button>
            <button type="button" onClick={() => setForm(null)} className="text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Cancel</button>
            {editing ? null : <span className="text-[11px] text-[color:var(--text-muted)]">Creates an alert on this eval and an agent that improves it.</span>}
          </div>
        </div>
  ) : null;

  return (
    <div data-testid="self-improving" className="grid gap-2">
      {pairs.length > 0 && editing ? formEl : pairs.length > 0 ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[11px] font-semibold ${st.live ? 'bg-success-soft text-success' : 'bg-[color:var(--surface-sunken)] text-[color:var(--text-secondary)]'}`} data-testid="self-improving-state">
              <span className={`h-1.5 w-1.5 rounded-full ${st.live ? 'bg-[color:var(--feedback-success)]' : 'border border-[color:var(--text-disabled)]'}`} aria-hidden />
              {st.live ? 'Live' : 'Paused'}
            </span>
            {!readOnly && (
              <button type="button" onClick={() => setLive(!st.live)} disabled={busy}
                className={`${btn} border border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover`}>
                {st.live ? <><Pause size={12} aria-hidden /> Pause</> : <><Play size={12} aria-hidden /> Resume</>}
              </button>
            )}
            {!readOnly && (
              <button type="button" onClick={openEdit} disabled={busy}
                className={`${btn} border border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover`}>
                <Pencil size={12} aria-hidden /> Edit
              </button>
            )}
            {!readOnly && (
              <button type="button" onClick={runNow} disabled={busy} title="Run the agent once now, with the alert's current value"
                className={`${btn} border border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover`}>
                <Zap size={12} aria-hidden /> Run now
              </button>
            )}
          </div>
          {pairs.map((p) => (
            <div key={p.alert.id} className="rounded-md border border-[color:var(--border-subtle)] px-3 py-2 text-[12px] text-[color:var(--text-secondary)]">
              <div>
                When <span className="font-medium text-[color:var(--text-primary)]">{measureWord(p.alert.measure).toLowerCase()}</span> is {opWord(p.alert.operator)}{' '}
                <span className="font-medium text-[color:var(--text-primary)]">{valueWord(p.alert.measure, p.alert.threshold)}</span> over {windowWord(p.alert.window_seconds)},
                Lucid looks at the results and proposes a change.
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-[11.5px]">
                <a href={`/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(p.alert.id)}`} className="inline-flex items-center gap-1 text-[color:var(--text-primary)] hover:underline">Alert <ExternalLink size={11} aria-hidden /></a>
                <a href={`/${encodeURIComponent(slug)}/agents/${encodeURIComponent(p.agent.id)}`} className="inline-flex items-center gap-1 text-[color:var(--text-primary)] hover:underline">Agent <ExternalLink size={11} aria-hidden /></a>
                {p.alert.last_triggered_at && <span className="text-[color:var(--text-muted)]">last fired {new Date(p.alert.last_triggered_at).toLocaleString()}</span>}
              </div>
              {/* An alert that fires and an agent that fails looks exactly
                  like an eval improving itself. It is worse than none at
                  all, so it says so here rather than only in the agent's
                  own run history. */}
              {p.agent?.last_run?.status === 'failed' && (
                <div role="alert" className="mt-1.5 rounded border border-[color:var(--feedback-error)] bg-danger-soft px-2 py-1.5 text-[11.5px] text-danger" data-testid="self-improving-last-run">
                  It last ran {new Date(p.agent.last_run.started_at).toLocaleString()} and failed, so nothing was proposed.
                  {p.agent.last_run.error ? <span className="mt-0.5 block font-mono text-[11px] opacity-90">{p.agent.last_run.error}</span> : null}
                </div>
              )}
            </div>
          ))}
        </>
      ) : !form ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-[color:var(--text-muted)]">Off. When the eval starts deciding badly, nothing looks into it.</span>
          {!readOnly && (
            <button type="button" onClick={openForm} className={`${btn} bg-brand text-ink-onBrand hover:bg-brand-hover`}>
              <AskIcon size={13} /> Deploy self-improving
            </button>
          )}
        </div>
      ) : (
        formEl
      )}
      {ran && <div className="text-[11.5px] text-[color:var(--text-secondary)]" role="status">{ran}</div>}
      {error && <div className="text-[11.5px] text-danger" role="alert">{error}</div>}
    </div>
  );
}
